import React, { useEffect, useState } from 'react';

import {
  Badge,
  Button,
  Center,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import {
  IconAlarm,
  IconChevronRight,
  IconHourglassEmpty,
  IconMoodConfuzed,
  IconRefresh,
  IconTag,
  IconTicket,
} from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import { useTracking } from 'hooks/mixpanel/useTracking';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BillingPlan,
  GymTitle,
  PackageCategoryAutoComplete,
  SalesPortalPackageList,
  SessionGym,
} from 'types';
import { PackageTypeLabelsEnum } from 'types/enums';
import { preserveQueryParams } from 'utils/navigation';

import PackageTypeDescription from './PackageTypeDescription';

export const getPackageSalesRemaining = (
  numValidSales: number,
  maxSalesAllowed: number | null,
): JSX.Element => {
  if (!maxSalesAllowed) {
    return <></>;
  }
  if (numValidSales < maxSalesAllowed) {
    return (
      <Text fw={500} color={numValidSales / maxSalesAllowed >= 0.75 ? 'red' : 'black'}>
        {maxSalesAllowed - numValidSales}/{maxSalesAllowed} Available
      </Text>
    );
  }

  return (
    <Text color='red' fw={700}>
      Sold Out
    </Text>
  );
};

const SalesPortalHomePagePackages = ({
  packages,
  packageCategory,
  setPackageCategory,
  gym,
  isAthleteAppView = false,
}: {
  packages: Array<SalesPortalPackageList> | undefined;
  packageCategory: PackageCategoryAutoComplete | null;
  setPackageCategory: (category: PackageCategoryAutoComplete | null) => void;
  gym?: GymTitle | SessionGym;
  isAthleteAppView?: boolean;
}): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useMantineTheme();
  const { trackSalesPortalPackageViewed } = useTracking();
  const { formatCurrency } = useLocalization(gym?.country, { isTaxInclusive: gym?.isTaxInclusive });
  const [filteredPackages, setFilteredPackages] = useState<Array<SalesPortalPackageList> | undefined>(
    packages,
  );

  const filterEnabledBillingPlans = (p: SalesPortalPackageList): BillingPlan[] => {
    if (isAthleteAppView) {
      return p.billingPlans.filter(bp => bp.athleteSalesPortalStatus === 'ENABLED');
    } else {
      return p.billingPlans.filter(bp => bp.salesPortalStatus === 'PUBLIC');
    }
  };

  const handlePackageSelect = (p: SalesPortalPackageList): void => {
    const enabledBillingPlans = filterEnabledBillingPlans(p);
    trackSalesPortalPackageViewed(p.gym, p.packageId, p.type, isAthleteAppView ? 'athlete_app' : 'public');

    // No purchasable plan: the "Unavailable" CTA still routes — the package page
    // renders the billing-plan error screen (spec Items 4 + 8) instead of checkout.
    const onlyOneBillingPlan = enabledBillingPlans.length === 1;
    const path = onlyOneBillingPlan
      ? `${p.packageId}/${enabledBillingPlans[0].billingPlanId}`
      : `${p.packageId}`;

    const pathWithQueryParams = preserveQueryParams(path, searchParams);
    navigate(pathWithQueryParams);
  };

  // Single-plan packages show the price directly; multi-plan packages show the
  // plan count and route through the pre-checkout plan modal (spec Item 10).
  const formatBillingPlan = (p: SalesPortalPackageList): JSX.Element => {
    const enabledBillingPlans = filterEnabledBillingPlans(p);
    if (enabledBillingPlans.length === 0) {
      return (
        <Group noWrap spacing='xs'>
          <IconTag size={theme.fontSizes.lg} />
          <Text size='xs'>No Billing Plans</Text>
        </Group>
      );
    } else if (enabledBillingPlans.length === 1) {
      return (
        <Text size='md' fw='bold' data-cy='packageCardPrice'>
          {formatCurrency(enabledBillingPlans[0].totalPrice / 100)}
        </Text>
      );
    } else {
      return (
        <Group noWrap spacing='xs' data-cy='packageCardPlanCount'>
          <IconTag size={theme.fontSizes.lg} />
          <Text size='xs'>{enabledBillingPlans.length} billing plans</Text>
        </Group>
      );
    }
  };

  useEffect(() => {
    if (packageCategory) {
      setFilteredPackages(packages?.filter(p => p.category === packageCategory?.name));
    } else {
      setFilteredPackages(packages);
    }
  }, [packageCategory, packages]);

  const gridStyle: React.CSSProperties | undefined = isAthleteAppView
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        paddingBottom: '1rem',
      }
    : undefined;

  return (
    <>
      {filteredPackages?.length === 0 && (
        <Center>
          <Stack align='center' spacing='sm' mt='2rem'>
            <IconMoodConfuzed size='50' />
            <Text>No packages found.</Text>
            <Button variant='light' onClick={() => setPackageCategory(null)}>
              Back to all
            </Button>
          </Stack>
        </Center>
      )}
      <SimpleGrid
        spacing='md'
        cols={4}
        breakpoints={[
          { maxWidth: 'xl', cols: 3, spacing: 'md' },
          { maxWidth: 'lg', cols: 2, spacing: 'sm' },
          { maxWidth: 'md', cols: 2, spacing: 'sm' },
          { maxWidth: 'sm', cols: 1, spacing: 'sm' },
        ]}
        style={gridStyle}
      >
        {filteredPackages?.map((p, i) => {
          const hasEnabledBillingPlans = filterEnabledBillingPlans(p).length > 0;
          return (
            <Paper
              key={i}
              data-cy='packageCard'
              shadow='lg'
              radius='md'
              withBorder
              p='md'
              style={{
                display: 'flex',
                flexDirection: 'column',
                ...(isAthleteAppView ? { maxWidth: '100%', minWidth: '100%' } : {}),
              }}
            >
              <Stack spacing='md' style={{ flexGrow: 1 }}>
                <Group align='start' position='apart' noWrap>
                  <Badge color='gray'>{p.category ? p.category : 'Uncategorized'}</Badge>
                  <Text size='sm'>{getPackageSalesRemaining(p.numValidSales, p.maxSalesAllowed)}</Text>
                </Group>

                <Title order={4} truncate>
                  {p.name}
                </Title>

                <Text
                  size='sm'
                  color='dark.3'
                  lineClamp={2}
                  sx={{ textOverflow: 'ellipsis', flexGrow: 1, width: '100%' }}
                >
                  {p.description}
                </Text>

                <SimpleGrid spacing='md' cols={2} sx={{ alignItems: 'end' }}>
                  <Stack spacing='xs'>
                    {p.autoRenew && (
                      <Group noWrap spacing='xs'>
                        <IconRefresh color={theme.colors.green[5]} size={theme.fontSizes.lg} />
                        <Text size='xs'>Auto-renew</Text>
                      </Group>
                    )}

                    <Group noWrap spacing='xs'>
                      {p.type === 'TIME_BASED' && <IconHourglassEmpty size={theme.fontSizes.lg} />}
                      {p.type === 'CREDIT_BASED' && <IconTicket size={theme.fontSizes.lg} />}
                      {p.type === 'TIME_OR_CREDIT_BASED' && <IconAlarm size={theme.fontSizes.lg} />}
                      <Text size='xs'>{PackageTypeLabelsEnum[p.type]}</Text>
                    </Group>
                    <PackageTypeDescription type={p.type} size='xs' />

                    {formatBillingPlan(p)}
                  </Stack>
                  <Group position='right'>
                    <Button
                      size='sm'
                      data-cy='packageCardCta'
                      // Public portal: "Unavailable" stays clickable and routes to the
                      // billing-plan error screen. The athlete-side portal has no such
                      // screen, so its CTA remains disabled.
                      disabled={isAthleteAppView && !hasEnabledBillingPlans}
                      variant={hasEnabledBillingPlans ? 'filled' : 'default'}
                      onClick={() => handlePackageSelect(p)}
                      rightIcon={hasEnabledBillingPlans ? <IconChevronRight /> : undefined}
                    >
                      {hasEnabledBillingPlans ? 'Get started' : 'Unavailable'}
                    </Button>
                  </Group>
                </SimpleGrid>
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>
    </>
  );
};

export default SalesPortalHomePagePackages;
